import Catalog from '../../../blocks.app/catalog/catalog'
import React, { useEffect, useState } from 'react'
import translate from '../../../core/translate'
import Content from 'blocks.app/content/content'
import styles from './report_dsActualization.jcss'
import Button from '../../../blocks.simple/button/button'
import { IDevice } from '../../../core/models/DigitalSignage'
import Sidebar from '../../../blocks.app/sidebar/sidebar'
import { api } from '../../../core/api/ConnectionManager'

type DeviceSyncInfo = {
    digitalSignageId: number
    unprocessedStatisticCount: number
    status: string
}

export const DsActualization = () => {
    const [devicesList, setDevicesList] = useState<DeviceSyncInfo[]>([])
    const [selectedItems, setSelectedItems] = useState<IDevice[]>([])
    const [lazyLoadedDevices, setLazyLoadedDevices] = useState<IDevice[]>([])

    const getDataList = () => {
        return devicesList.map((item) => {
            return {
                id: item.digitalSignageId,
                fields: [
                    {
                        id: 'notActualizedObjects',
                        value: item.unprocessedStatisticCount,
                    },
                    {
                        id: 'progressStatus',
                        value: translate(item.status),
                    },
                ],
            }
        })
    }

    const renderCustomComponent = () => {
        return (
            <Button
                mod="fill"
                className={styles.button}
                onClick={!actualizationIsActive() ? startActualization : cancelActualization}
                disabled={actualizationIsActive() ? false : !selectedItems.length}
            >
                {translate(!actualizationIsActive() ? 'actualize' : 'cancel')}
            </Button>
        )
    }

    const actualizationIsActive = () =>
        devicesList.length && devicesList.some((device) => device.status !== 'idle' && device.status !== 'complete')

    const onGetItems = (items: IDevice[]) => {
        const filteredItems = items.filter((item: IDevice) => item.type === 'digitalSignage')

        setLazyLoadedDevices([...lazyLoadedDevices, ...filteredItems])
        getActualization(filteredItems)
    }

    const startActualization = () => {
        api.send('dsStartActualizationData', {
            digitalSignageId: selectedItems.filter((item) => item.type === 'digitalSignage').map((device) => device.id),
            groupId: selectedItems.filter((item) => item.type === 'group').map((group) => group.id),
        })
    }

    const getActualization = (devices: IDevice[]) => {
        const digitalSignageId = devices.filter((device) => device.type === 'digitalSignage').map((device) => device.id)

        if (!digitalSignageId.length) return

        api.send('dsGetActualizationData', {
            digitalSignageId,
        })
    }

    const cancelActualization = () => {
        api.send('dsCancelActualizationData', {})
    }

    useEffect(() => {
        api.send('dsGetCurrentActualizationData', {}).then((res: any) => {
            if (!res || !res.length) return

            const devicesList = res.map((device: DeviceSyncInfo) => {
                const item = lazyLoadedDevices.find((item) => device.digitalSignageId === item.id)

                return {
                    digitalSignageId: device.digitalSignageId,
                    status: device.status,
                    unprocessedStatisticCount: item ? item.unprocessedStatisticCount : null,
                }
            })

            setDevicesList(devicesList)
        })

        const listenersId: string[] = []

        api.addObserver(
            'dsActualizationDataReceived',
            (list: any) => {
                if (!list) return

                setDevicesList(list)
            },
            listenersId
        )

        return () => {
            listenersId.forEach((id: any) => api.removeObserver(id))
        }
    }, [])

    return (
        <Content
            toolbar={
                <Sidebar
                    selectedSide="right"
                    onChange={(list: any) => {
                        setSelectedItems(list)
                    }}
                    content={{
                        right: {
                            title: `${translate('countOfSelectedDevices')}: ${selectedItems ? selectedItems.length : ''}`,
                            options: selectedItems,
                        },
                    }}
                    isEmpty={{
                        right: !selectedItems.length,
                    }}
                    emptyText={{
                        right: {
                            title: '',
                            description: translate('selectDevicesForActualization'),
                            wiki: {
                                blockKey: 'reports',
                                blockSubKey: 'actualization',
                                infoKey: 'aboutActualization',
                            },
                        },
                    }}
                    onSelectInfo={() => {}}
                />
            }
            title={translate('deviceActualization')}
            wrapperClassName={styles.reportsWrapper}
        >
            <Catalog
                type={'displays'}
                searchbarMod={'withShadow'}
                selectedItems={selectedItems}
                onSelect={(selectedItems: IDevice[]) => {
                    setSelectedItems(selectedItems)
                }}
                disposition={'list'}
                hideView={true}
                hideSelection={true}
                customData={{
                    fields: [
                        { id: 'id', name: translate('id') },
                        { id: 'name', name: translate('name') },
                        { id: 'lastActualizationDate', name: translate('lastActualizationDate'), useRawData: true },
                        { id: 'notActualizedObjects', name: translate('notActualizedObjects') },
                        { id: 'progressStatus', name: translate('progressStatus') },
                    ],
                    alterFieldValue: {
                        notActualizedObjects: '—',
                        progressStatus: translate('idle'),
                    },
                    exceptionParams: [{ name: 'type', value: 'group' }],
                    dataList: getDataList(),
                }}
                customComponent={renderCustomComponent}
                onGetItems={onGetItems}
                selectAllCheckbox={true}
            />
        </Content>
    )
}
